import { Injectable, inject, signal } from '@angular/core';
import { ILesson, LESSONS_TOKEN } from '../components/lesson-mini-components/components/lessons.clases';
import { NOTION_SERVICE_TOKEN, NotionExportType } from '../models/notion.models';
import { TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core';
import { DefaultLessonsService } from './default-lessons.service';

@Injectable() // Consider providing in root or specific module/component
export class LessonNotionService {
  #notionService = inject(NOTION_SERVICE_TOKEN);
  public lessonsService = inject(LESSONS_TOKEN, { optional: true }) ?? inject(DefaultLessonsService);
  #toastService = inject(TOAST_ALERTS_TOKEN);

  // Keep track of loading state specific to Notion operations
  isLoading = signal(false);

  /**
   * Extracts the Notion Page ID from a URL.
   * @param url The Notion page URL.
   * @returns The extracted page ID or null if invalid.
   */
  private extractNotionPageId(url: string): string | null {
    const notionIdRegex = /[a-f0-9]{32}(?=\?|$)/;
    const match = url.match(notionIdRegex);
    const notionId = match ? match[0] : null;
    if (!notionId) {
      this.#toastService.error({
        title: 'URL inválido',
        subtitle: 'Por favor ingresa una URL válida de Notion.',
      });
      return null;
    }
    return notionId;
  }

  /**
   * Links an existing lesson with a Notion page ID by updating the lesson's extras.
   * @param lesson The current lesson data.
   * @param notionPageId The Notion page ID to link.
   * @returns The updated lesson data from the backend or undefined on failure.
   */
  public async linkLessonWithNotion(lesson: ILesson, notionPageId: string): Promise<ILesson | undefined> {
    if (!lesson || !lesson.id) {
      // Ensure lesson exists and has an ID
      this.#toastService.warn({ title: 'No se puede enlazar', subtitle: 'La lección debe existir y tener un ID.' });
      return undefined;
    }

    const updatedLesson = {
      ...lesson,
      extras: {
        ...(lesson.extras || {}),
        notionPageId: notionPageId,
      },
    };

    this.isLoading.set(true);
    try {
      const savedLesson = await this.lessonsService.postLesson(updatedLesson);
      this.#toastService.success({ title: 'Listo', subtitle: 'Se enlazó la lección con Notion.' });
      return savedLesson;
    } catch (error) {
      // Remove explicit type, rely on tsconfig setting
      console.error('Error linking with Notion:', error);
      this.#toastService.error({ title: 'Error al enlazar', subtitle: 'Ocurrió un error inesperado.' });
      return undefined;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Handles the process of importing lesson content from Notion.
   * It prompts the user for a URL if necessary, extracts the ID, fetches content,
   * and potentially links the lesson if it's an existing one.
   * @param currentLesson The current lesson data (can be a new or existing lesson).
   * @param lessonId The current lesson ID (null if it's a new lesson).
   * @returns The fetched HTML content from Notion, or null if the process fails.
   */
  public async importAndLinkLessonFromNotion(currentLesson: ILesson | undefined, lessonId: string | null): Promise<string | null> {
    if (!currentLesson) return null;

    let notionPageId: string | null = null;

    if (currentLesson.extras?.notionPageId) {
      const useExisting = confirm(`Ya tenemos el id ${currentLesson.extras.notionPageId} ¿Quieres usar este id para importar?`);
      if (useExisting) {
        notionPageId = currentLesson.extras.notionPageId;
      } else {
        const inputUrl = prompt('Ingresa la NUEVA URL de Notion para importar (este ID NO se guardará automáticamente si la lección ya existe)');
        if (!inputUrl) return null; // User cancelled
        notionPageId = this.extractNotionPageId(inputUrl);
      }
    } else {
      const inputUrl = prompt('Ingresa el URL de Notion para importar la lección (se enlazará si la lección ya existe)');
      if (!inputUrl) return null; // User cancelled
      notionPageId = this.extractNotionPageId(inputUrl);
      // Link automatically only if we got a valid Notion ID AND the lesson already exists (has an ID)
      if (notionPageId && lessonId) {
        const linkedLesson = await this.linkLessonWithNotion(currentLesson, notionPageId);
        if (!linkedLesson) {
          // Linking failed, maybe stop the import? Or proceed without linking?
          // For now, let's stop.
          this.#toastService.error({ title: 'Error de Enlace', subtitle: 'No se pudo enlazar con Notion antes de importar.' });
          return null;
        }
        // If linking succeeded, the lesson object might have changed, but we proceed with the import using the notionPageId.
      }
    }

    if (!notionPageId) {
      this.#toastService.warn({ title: 'Sin ID de Notion', subtitle: 'No se proporcionó un ID de Notion válido para importar.' });
      return null;
    }

    this.isLoading.set(true);
    try {
      this.#toastService.info({ title: 'Importando lección...', subtitle: 'Espera unos segundos' });
      const md = await this.#notionService.getPageInSpecificFormat(notionPageId, NotionExportType.HTML);
      console.log('Imported MD/HTML:', md);
      this.#toastService.success({ title: 'Contenido Importado', subtitle: 'Contenido de Notion obtenido.' });
      return md.content; // Return the fetched content
    } catch (error) {
      // Remove explicit type, rely on tsconfig setting
      console.error('Error importing from Notion:', error);
      this.#toastService.error({ title: 'Error de importación', subtitle: 'Ocurrió un error inesperado.' });
      return null; // Return null on failure
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Fetches content from the linked Notion page for AI improvement (placeholder).
   * @param lesson The current lesson data.
   */
  public async improveLessonWithNotionAI(lesson: ILesson | undefined): Promise<void> {
    if (!lesson) return;
    const notionId = lesson.extras?.notionPageId;

    if (!notionId) {
      this.#toastService.warn({ title: 'Sin ID de Notion', subtitle: 'Enlaza la lección con Notion primero.' });
      return;
    }

    this.isLoading.set(true);
    try {
      this.#toastService.info({ title: 'Mejorando con IA...', subtitle: 'Obteniendo contenido de Notion.' });
      const md = await this.#notionService.getPageInSpecificFormat(notionId, NotionExportType.HTML);
      console.log('Content to improve:', md);
      // TODO: Add actual AI improvement logic here
      // e.g., call another service: await this.aiImprovementService.improve(md.content);
      // Then potentially update the lesson via lessonService or return data
      this.#toastService.success({ title: 'Contenido Obtenido', subtitle: 'Listo para mejorar con IA (lógica no implementada).' });
    } catch (error) {
      // Remove explicit type, rely on tsconfig setting
      console.error('Error improving with AI:', error);
      this.#toastService.error({ title: 'Error de IA', subtitle: 'Ocurrió un error inesperado.' });
    } finally {
      this.isLoading.set(false);
    }
  }
}
